import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Link, TablePagination } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import api from "api";
import { Loading, ErrorBox, Pane, HFlex, VFlex, Text } from "shared";
import { isUsMarket } from "utils/market";
import { useReferralTracking, useUpdateReferral, useBulkUpdateReferrals, exportReferralsCsv } from "./queries";
import { ReferralTrackingParams } from "./queries/types";
import { PAGE_SIZE_OPTIONS } from "./constants";
import ReferralTrackingTable from "./components/ReferralTrackingTable";
import ReferralTrackingFilters from "./components/ReferralTrackingFilters";
import ReferralDetailDrawer from "./components/ReferralDetailDrawer";
import BulkEditModal from "./components/BulkEditModal";
import { Referral } from "./queries/types";
import buildQueryString from "./queries/buildQueryString";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2, 3, 4),
  },
  paper: {
    overflow: "hidden",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  bulkBar: {
    padding: theme.spacing(1, 2),
    backgroundColor: "rgba(80, 150, 232, 0.08)",
    borderTop: "1px solid rgba(80, 150, 232, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

const getDateRangeFromPreset = (preset: string): { start: string; end: string } | null => {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return { start: fmt(today), end: fmt(today) };
    case "this_week": {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return { start: fmt(weekStart), end: fmt(today) };
    }
    case "last_30": {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      return { start: fmt(d), end: fmt(today) };
    }
    case "last_90": {
      const d = new Date(today);
      d.setDate(d.getDate() - 90);
      return { start: fmt(d), end: fmt(today) };
    }
    default:
      return null;
  }
};

const ReferralTrackingPage: React.VFC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("referral_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [datePreset, setDatePreset] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [allPagesSelected, setAllPagesSelected] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [drawerReferral, setDrawerReferral] = useState<Referral | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Compute date range from preset or custom
  const dateRange =
    datePreset === "custom" && dateRangeStart && dateRangeEnd
      ? { start: dateRangeStart, end: dateRangeEnd }
      : datePreset
        ? getDateRangeFromPreset(datePreset)
        : null;

  const queryParams: ReferralTrackingParams = useMemo(() => ({
    offset: page * perPage,
    limit: perPage,
    sort_by: sortBy,
    sort_order: sortDirection,
    search: debouncedSearch || undefined,
    filters: Object.keys(filters).length > 0 ? filters : undefined,
    date_from: dateRange?.start,
    date_to: dateRange?.end,
  }), [page, perPage, sortBy, sortDirection, debouncedSearch, filters, dateRange?.start, dateRange?.end]);

  const { data, isLoading, error } = useReferralTracking(queryParams);
  const updateReferral = useUpdateReferral();
  const bulkUpdate = useBulkUpdateReferrals();

  // Keep drawer referral in sync with refetched data
  useEffect(() => {
    if (drawerReferral && data?.items) {
      const updated = data.items.find((r) => r.id === drawerReferral.id);
      if (updated) setDrawerReferral(updated);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateReferralRef = useRef(updateReferral.mutate);
  updateReferralRef.current = updateReferral.mutate;
  const bulkUpdateRef = useRef(bulkUpdate.mutateAsync);
  bulkUpdateRef.current = bulkUpdate.mutateAsync;

  const handleSortChange = useCallback(
    (field: string) => {
      if (field === sortBy) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDirection("asc");
      }
      setPage(0);
    },
    [sortBy]
  );

  const handleSelectionChange = useCallback((ids: Set<number>) => {
    setSelectedIds(ids);
    setAllPagesSelected(false);
  }, []);

  const handleSelectAllPages = useCallback(async () => {
    const totalCount = data?.total_count || 0;
    if (totalCount === 0) return;

    const PAGE_SIZE = 500;
    const allIds = new Set<number>();

    try {
      for (let off = 0; off < totalCount; off += PAGE_SIZE) {
        const qs = buildQueryString({ ...queryParams, offset: off, limit: PAGE_SIZE });
        const response = await api.call(`/uk/referral_tracking${qs}`);
        const result = await response.json();
        const items: Array<{ id?: number; medical_referral_id?: number }> = Array.isArray(result)
          ? result
          : result.items ?? [];
        items.forEach((r) => {
          const id = r.id ?? r.medical_referral_id ?? 0;
          if (id) allIds.add(id);
        });
        if (items.length < PAGE_SIZE) break;
      }
      setSelectedIds(allIds);
      setAllPagesSelected(true);
    } catch {
      // Fallback: keep current selection
    }
  }, [data?.total_count, queryParams]);

  const handleCellSave = useCallback(
    (id: number, field: string, value: string | null) => {
      updateReferralRef.current({ id, field, value });
    },
    []
  );

  const handleBulkConfirm = useCallback(
    async (fields: Record<string, string | null>) => {
      const allIds = Array.from(selectedIds);
      const CHUNK_SIZE = 500;
      for (let i = 0; i < allIds.length; i += CHUNK_SIZE) {
        const chunk = allIds.slice(i, i + CHUNK_SIZE);
        await bulkUpdateRef.current({ ids: chunk, fields });
      }
    },
    [selectedIds]
  );

  const handleExport = () => {
    exportReferralsCsv(queryParams);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleFiltersChange = (newFilters: Record<string, string[]>) => {
    setFilters(newFilters);
    setPage(0);
    setSelectedIds(new Set());
    setAllPagesSelected(false);
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    setPage(0);
    setSelectedIds(new Set());
    setAllPagesSelected(false);
  };

  if (isUsMarket) return null;

  return (
    <VFlex gap={2} className={classes.root}>
      <HFlex justifyContent="space-between" alignItems="flex-start">
        <VFlex gap={0.5}>
          <Text bold large>{t("referral_tracking.title")}</Text>
          <Text secondary tiny>{t("referral_tracking.subtitle")}</Text>
        </VFlex>
        <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExport}>
          {t("referral_tracking.export_csv")}
        </Button>
      </HFlex>

      <Pane zeroBorderRadius p={0} className={classes.paper}>
        <ReferralTrackingFilters
          search={search}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          datePreset={datePreset}
          onDatePresetChange={handleDatePresetChange}
          onDateRangeChange={handleDateRangeChange}
        />

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <Box className={classes.bulkBar}>
            <Text tiny bold>
              {allPagesSelected
                ? `All ${data?.total_count || selectedIds.size} selected`
                : `${selectedIds.size} selected`}
            </Text>
            {!allPagesSelected && data && selectedIds.size === data.items.length && data.total_count > data.items.length && (
              <Link component="button" variant="body2" onClick={handleSelectAllPages}>
                Select all {data.total_count} across pages
              </Link>
            )}
            <Button size="small" variant="contained" color="primary" onClick={() => setBulkEditOpen(true)}>
              {t("referral_tracking.bulk_edit")}
            </Button>
            <Button size="small" onClick={() => { setSelectedIds(new Set()); setAllPagesSelected(false); }}>
              {t("referral_tracking.clear_selection")}
            </Button>
          </Box>
        )}

        {error && <ErrorBox error={error} centered verbose mt={2} mb={2} />}

        {isLoading ? (
          <Loading mt={8} mb={8} />
        ) : (
          <ReferralTrackingTable
            referrals={data?.items || []}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            selectedIds={selectedIds}
            onSelectionChange={handleSelectionChange}
            onCellSave={handleCellSave}
            onRowClick={setDrawerReferral}
          />
        )}

        <Box className={classes.footer}>
          <TablePagination
            component="div"
            count={data?.total_count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={perPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </Box>
      </Pane>

      <BulkEditModal
        open={bulkEditOpen}
        selectedCount={selectedIds.size}
        onClose={() => setBulkEditOpen(false)}
        onConfirm={handleBulkConfirm}
      />

      <ReferralDetailDrawer
        referral={drawerReferral}
        onClose={() => setDrawerReferral(null)}
        onCellSave={handleCellSave}
      />
    </VFlex>
  );
};

export default ReferralTrackingPage;
